import cn from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/common/Button';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { EnumRoutePaths } from 'src/configs/routes';
import { convertAllTagsArr, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { useBannerActions } from 'src/hooks/useBannerActions';
import { MediaManager } from 'src/manager/MediaManager';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { setSelectedCategory } from 'src/store/app/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { setSelectedGame, setSelectedProvider, setSelectedTag } from 'src/store/games/actions';
import { toggleMobileMenu } from 'src/store/mobile/actions';
import { CmsCta } from 'src/types/cms/cmsFieldTypes';
import { CmsMenuWidgetItem } from 'src/types/cms/cmsWidgetTypes';
import { NavigationItem } from 'src/types/res-dto/categories-res';
import { RootState } from 'src/types/store-types';
import Language from '../../Language';
import { Logo } from '../../Logo';
import StaticLists from './StaticLists';
import _styles from './styles.module.scss';

const iconsPath = '/categoryIcons/';

type Props = {
  menuWidgetItems: CmsMenuWidgetItem[];
};

const MobileMenu: FC<Props> = ({ menuWidgetItems }) => {
  const { t }: Translation = useTranslation();
  const navigate = useNavigate();
  const categoryMenuRef: any = useRef();
  const { locale } = useI18n();
  const sessionRecorder = useSessionRecorder();
  const { promotionAction, categoryAction, gameBoxAction, refFriendAction } = useBannerActions();

  const _toggleMobileMenu = useConnectedAction(toggleMobileMenu);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const _selectedGame = useConnectedAction(setSelectedGame);
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _openAccountModal = useConnectedAction(openMyAccountModal);

  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { freeSpinGames } = useSelector((state: RootState) => state.integrations);
  const { user } = useSelector((state: RootState) => state.user);
  const { selectedTag } = useSelector((state: RootState) => state.games);
  const { generalConfigs, categoriesData, navigation, tagsMetaInfo } = useSelector((state: RootState) => state.configs);
  const blogUrl = process.env.REACT_APP_BLOG_URL as string;
  const [selectMenuItem, setSelectedMenuItem] = useState<string>('');

  setBodyOverflow('unset');

  const selectTag = (tag: string): void => {
    _setSelectedTag(tag);
    _setSelectedProvider(['all_providers']);
    navigate(`/category/${selectMenuItem}`);
    closeCategoriesMenu();
  };
  const homeTag = useMemo(() => {
    return navigation.filter((f) => f.name === selectedCategory)[0];
  }, [selectedCategory, navigation]);

  useEffect(() => {
    if (!!categoriesData && !!selectedCategory && !!homeTag && locale) {
      convertAllTagsArr(categoriesData, selectedCategory, homeTag, tagsMetaInfo, locale);
    }
  }, [categoriesData, selectedCategory, homeTag, tagsMetaInfo, locale]);

  const ctaAction = useCallback(
    (cta?: CmsCta): void => {
      if (!cta) return;
      closeCategoriesMenu();

      switch (cta.variant) {
        case 'goToPromotion':
          promotionAction(cta.param);
          return;
        case 'goToCategory':
          categoryAction(cta.param);
          return;
        case 'goToGameBox':
          gameBoxAction();
          return;
        case 'goToRefFriend':
          refFriendAction();
          return;
        case 'noAction':
          return;
        default:
          return;
      }
    },
    [categoryAction, promotionAction, gameBoxAction, refFriendAction]
  );

  const selectGame = (game: any): void => {
    const _game = navigation?.find((f) => f.name === game);

    _selectedGame({ id: _game?.gameId });
    closeCategoriesMenu();

    if (isMobile) {
      setBodyOverflow('unset');
    }
  };

  const selectReferAFriend = (): void => {
    if (user) {
      sessionRecorder?.trackEvent('"Invite a friend" button click', {
        placement: 'sidebar',
      });
      _openAccountModal({ section: 'referAFriend', subSection: 'referAFriend' });
      closeCategoriesMenu();
    } else {
      _openAuthModal('login');
    }
  };

  const selectPromotion = (): void => {
    closeCategoriesMenu();
    navigate(EnumRoutePaths.PROMOTIONS);
  };

  const selectBlog = (): void => {
    closeCategoriesMenu();
    navigate(EnumRoutePaths.BLOG);
  };

  const selectCategory = (menuItem: string, type = 'category'): void => {
    closeCategoriesMenu();
    _setSelectedProvider(['all_providers']);
    navigate(`/${type}/${menuItem}`);

    _setSelectedCategory(menuItem);
    _setSelectedTag('');
  };

  const transformCategoryName = (menuItem: string): string => {
    let category;

    if (menuItem === 'betting') {
      category = 'sport';
    } else if (menuItem === 'tournament') {
      category = 'casinoTournament';
    } else {
      category = menuItem;
    }
    return category;
  };

  const checkSubType = (_navItemData: NavigationItem | undefined, menuItem: string): void => {
    if (_navItemData && (_navItemData?.subType === 'staticPage' || !categoriesData)) {
      selectCategory(menuItem);
    } else {
      !selectedTag && categoriesData && _setSelectedTag(categoriesData[menuItem]?.tags?.[0].tagName);
    }
  };

  const isSimpleCategory = (menuItem: string): boolean => {
    const tagsLength = categoriesData?.[menuItem]?.tags?.length ?? 0;
    const excludedItems = ['silvaSeries', 'dinhutech', 'crashGames', 'virtualSport', 'tigerFortune', 'fun-mission'];

    return tagsLength < 2 || excludedItems.includes(menuItem) || (tagsLength > 2 && !hasChildren(menuItem));
  };

  const staticNavigationAction = (menuItem: string, category: string): void => {
    if (menuItem === 'blog') {
      selectBlog();
    } else {
      selectPromotion();
    }
    setSelectedMenuItem(category === selectMenuItem ? '' : category);
    _setSelectedCategory(menuItem);
  };

  const actionByNavigationType = (type: string, menuItem: string, _navItemData: NavigationItem): void => {
    const category = transformCategoryName(menuItem);
    localStorage.setItem('selectedProvider', 'all_providers');
    switch (type) {
      case 'category':
      case 'sport':
      case 'fun-mission':
        if (categoriesData && isSimpleCategory(menuItem)) {
          selectCategory(menuItem);
        } else {
          checkSubType(_navItemData, menuItem);
        }
        if (type === 'category') {
          sessionRecorder?.trackEvent('Category navigation click', {
            categoryName: _navItemData.name,
            categoryType: _navItemData.type,
          });
        }

        setSelectedMenuItem(category === selectMenuItem ? '' : category);
        break;
      case 'content':
        selectCategory(menuItem, 'content');
        break;
      case 'game':
        selectGame(menuItem);
        break;
      case 'race':
      case 'fsRace':
      case 'tombala':
      case 'casinoTournament':
      case 'cybersport':
      case 'safe-promo':
      case 'raffleDraw':
        selectCategory(menuItem);
        setSelectedMenuItem(category === selectMenuItem ? '' : category);
        break;
      case 'refer':
        selectReferAFriend();
        break;
      case 'static':
        staticNavigationAction(menuItem, category);
        break;
      case 'externalLink':
        window.open(_navItemData.redirectionUrl, '_blank');
        break;
      default:
        setSelectedMenuItem(category === selectMenuItem ? '' : category);
    }
  };

  const changeSelectedMenuItem = (menuItem: string, type = ''): void => {
    const _user = localStorage.getItem('user');
    if (menuItem !== 'language') {
      const _navItemData = navigation.find((f) => f.name === menuItem) as NavigationItem;

      if (_navItemData?.needLogIn && !_user) {
        localStorage.setItem('menuItem', menuItem);
        _openAuthModal('login');
      } else {
        if (hasBlog && blogUrl && menuItem === 'blog') {
          window.open(blogUrl, '_blank', 'noopener,noreferrer');
        } else {
          actionByNavigationType(type, menuItem, _navItemData);
        }
      }
    }
  };

  const hasChildren = (category: string): boolean => {
    if (categoriesData) {
      return Boolean(
        categoriesData[category]?.tags?.length > 1 && navigation?.find((f: any) => f.name === category)?.hasHomeTag
      );
    }
    return false;
  };

  const closeCategoriesMenu = (): void => {
    setBodyOverflow('set');
    setSelectedMenuItem('');
    _toggleMobileMenu();
  };

  const categoriesList = useMemo(() => {
    return navigation
      .filter((n) => n.showInNavigation)
      .filter((n) => n.name !== 'liveSport')
      .filter((n) => n.name !== 'blog')
      .filter((n) => n.name !== 'casinoTournament')
      ?.map((m) => {
        if (m.name === 'sport') {
          m.name = 'betting';
        }
        return m;
      });
  }, [navigation, generalConfigs]);

  const renderTags = (category: { name: string }): JSX.Element => {
    return (
      <>
        {categoriesData &&
          categoriesData[category.name]?.tags?.map((tag, j) => {
            const tagName = tag?.tagTrName ? tag?.tagTrName : tag.tagName;
            const isSelected = selectedTag === tagName;
            const tagStyle = {
              color: isSelected ? 'var(--text-brand)' : 'var(--text-primary)',
            };

            return (
              <li
                key={j + tagName}
                style={tagStyle}
                onClick={() => {
                  selectTag(tagName);
                  _setSelectedCategory(category.name);
                }}
              >
                {tag?.tagTrName ? tag?.tagTrName : t(tag.tagName)}
              </li>
            );
          })}
      </>
    );
  };

  const renderCategory = useCallback(
    (category: any) => {
      return (
        <React.Fragment key={category.name}>
          <Button
            className={cn(_styles.accordion, 'mobile_menu_accordion')}
            onClick={() => changeSelectedMenuItem(category.slug || category.name, category.type)}
            justifyContentValue="space-between"
          >
            <div className={_styles.inner_accordion}>
              <div>
                {category?.imageSource &&
                  (category.imageSource.includes('.svg') ? (
                    <SvgIcon
                      src={MediaManager.getSrcFromMediaAssets(category.imageSource, iconsPath)}
                      className={cn(_styles.category_icon, 'mobile_menu_category_icon')}
                    />
                  ) : (
                    <img
                      src={MediaManager.getSrcFromMediaAssets(category.imageSource, iconsPath)}
                      alt={category.name}
                    />
                  ))}
                {t(`category_${category.name}${category.type === 'game' ? 'game' : ''}`)}
                {category?.label && (
                  <img
                    src={MediaManager.getSrcFromLocaleFolder(category?.label, '/labels/')}
                    className={_styles.label}
                  />
                )}
              </div>
              {hasChildren(category.name) && (
                <SvgIcon
                  src={icons.arrowDown}
                  className={cn(_styles.arrow, { [_styles.rotateArrow]: selectMenuItem === category.name })}
                />
              )}
            </div>
          </Button>
          {selectMenuItem === category.name && hasChildren(category.name) && (
            <ul className={_styles.panel}>
              {freeSpinGames && Object.values(freeSpinGames).length > 0 && selectedCategory === 'casino' && (
                <li
                  style={{ color: selectedTag === 'free_rounds' ? 'var(--text-brand)' : 'var(--text-primary)' }}
                  onClick={() => selectTag('free_rounds')}
                >
                  {t('free_rounds')}
                </li>
              )}
              {renderTags(category)}
            </ul>
          )}
        </React.Fragment>
      );
    },
    [selectMenuItem, categoriesData]
  );

  const hasBlog = useMemo(() => {
    return !!navigation.find((f) => f.name === 'blog' && f?.subType !== 'staticPage');
  }, []);

  const hasPromotion = useMemo(() => {
    return !!navigation.find((f) => f.name === 'promotions' && f?.subType !== 'staticPage');
  }, []);

  return (
    <div className={cn(_styles.mobile_menu_container)} ref={categoryMenuRef}>
      <div>
        <div className={_styles.logo}>
          <Logo settings={{ src: 'logo.png' }} />
        </div>
        <div className={_styles.menu_icon_wrapper}>
          <p className={_styles.title}>{t('menu')}</p>
          <Button className={_styles.close_menu_wrapper} onClick={closeCategoriesMenu} icon={icons.close} />
        </div>
      </div>
      <div className={_styles.menu_content_wrapper}>
        <div className={_styles.promotions_section}>
          {generalConfigs?.hasMobileMenuPromotions &&
            menuWidgetItems.length > 0 &&
            menuWidgetItems.map((item) => (
              <Button
                key={item.category}
                className={cn(_styles.promotions_section_btn)}
                type="button"
                onClick={() => ctaAction(item.action)}
              >
                <img src={item.imageMobile?.path ?? ''} alt={item.category} />
              </Button>
            ))}
        </div>
        {categoriesList.map((category: any) => renderCategory(category))}
        {hasPromotion && (
          <Button
            className={cn(_styles.accordion, 'mobile_menu_accordion')}
            onClick={() => changeSelectedMenuItem('promotions', 'static')}
            justifyContentValue="space-between"
          >
            <div className={_styles.promotions}>
              <span className="icon-promos mobile_menu_promo_icon" />
              {t('button_promos')}
            </div>
          </Button>
        )}

        {hasBlog && (
          <Button
            className={cn(_styles.accordion, 'mobile_menu_accordion')}
            onClick={() => changeSelectedMenuItem('blog', 'static')}
            justifyContentValue="space-between"
          >
            <div className={_styles.promotions}>
              <SvgIcon
                src={MediaManager.getSrcFromMediaAssets('blog.svg', iconsPath)}
                className={cn(_styles.category_icon, 'mobile_menu_category_icon')}
              />
              {t('blog')}
            </div>
          </Button>
        )}
        <div className={_styles.underLiner} />
        {!!generalConfigs?.languages?.length && (
          <Button
            className={cn(_styles.accordion, _styles['lg-item'], 'mobile_menu_accordion')}
            onClick={() => changeSelectedMenuItem('language')}
          >
            <div className={cn(_styles.menu_content_text)}>
              <div>
                <span className="icon-language" />
                {t('language')}
              </div>
              <Language />
            </div>
          </Button>
        )}

        {!generalConfigs?.removeInfoAndRulesFromMobileMenu && (
          <StaticLists
            changeSelectedMenuItem={changeSelectedMenuItem}
            selectMenuItem={selectMenuItem}
            openInfoAndRules={_openAccountModal}
          />
        )}
      </div>
    </div>
  );
};
export default MobileMenu;
